/**
 * VincularHospitaisModal
 *
 * Modal para vincular hospitais ao cliente usando dual-list (igual ao RepresentanteFormModal)
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Save, Building2, Search, ArrowRight, ArrowLeft, Loader2, MapPin } from 'lucide-react';
import clienteService from '../services/clienteService';
import { msg } from '../utils/messages';
import { useConfirm } from '../hooks/useConfirm';
import { cnpjMatch } from '../utils/cnpjMatch';  // Story 2.38

interface Hospital {
  id: number;
  codigo: string;
  nome: string;
  cnpj: string;
  cidade: string;
  estado: string;
}

interface HospitalVinculado {
  hospital_id: number;
  hospital_nome: string;
  hospital_codigo: string;
  hospital_cidade?: string;
  hospital_estado?: string;
  data_inicio: string;
  codigo?: string;
}

interface VincularHospitaisModalProps {
  clienteId: number;
  hospitaisVinculados: number[]; // IDs dos hospitais já vinculados
  onClose: () => void;
  onSave: () => void;
}

const VincularHospitaisModal: React.FC<VincularHospitaisModalProps> = ({
  clienteId,
  hospitaisVinculados: hospitaisVinculadosIds,
  onClose,
  onSave,
}) => {
  const { confirm, ConfirmDialog } = useConfirm();

  // State principal
  const [availableHospitais, setAvailableHospitais] = useState<Hospital[]>([]);
  const [linkedHospitais, setLinkedHospitais] = useState<HospitalVinculado[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // State de seleção
  const [selectedAvailable, setSelectedAvailable] = useState<number[]>([]);
  const [selectedLinked, setSelectedLinked] = useState<number[]>([]);

  // State de busca independente
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');

  // State de operações
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  // Carregar dados
  const loadHospitais = useCallback(async () => {
    try {
      setLoadingData(true);

      // Carregar todos os hospitais disponíveis
      const todosHospitais = await clienteService.getTodosHospitais();
      setAvailableHospitais(todosHospitais);

      // Carregar hospitais já vinculados ao cliente
      const vinculados = await clienteService.getClienteHospitais(clienteId);
      const formattedVinculados = Array.isArray(vinculados)
        ? vinculados.map((h: any) => ({
            hospital_id: h.hospital_id,
            hospital_nome: h.hospital_nome,
            hospital_codigo: h.hospital_codigo,
            hospital_cidade: h.hospital_cidade,
            hospital_estado: h.hospital_estado,
            data_inicio: h.data_inicio,
            codigo: h.codigo,
          }))
        : [];
      setLinkedHospitais(formattedVinculados);

    } catch (error) {
      console.error('Erro ao carregar hospitais:', error);
      msg.error('Erro ao carregar hospitais');
    } finally {
      setLoadingData(false);
    }
  }, [clienteId]);

  useEffect(() => {
    loadHospitais();
  }, [loadHospitais]);

  // Filtros de hospitais
  const filteredAvailable = useMemo(() => {
    const linkedIds = new Set(linkedHospitais.map((h) => h.hospital_id));
    const normalizedSearch = searchLeft.trim().toLowerCase();

    return availableHospitais
      .filter((hospital) => !linkedIds.has(hospital.id))
      .filter((hospital) => {
        if (!normalizedSearch) return true;

        const haystack = `${hospital.nome_fantasia ?? ''} ${hospital.codigo ?? ''} ${hospital.cidade ?? ''} ${hospital.estado ?? ''}`.toLowerCase();
        // Story 2.38: busca por CNPJ normalizada (ignora ./- /)
        return haystack.includes(normalizedSearch) || cnpjMatch(hospital.cnpj, searchLeft);
      })
      .sort((a, b) => (a.nome_fantasia || '').localeCompare(b.nome_fantasia || ''));
  }, [linkedHospitais, availableHospitais, searchLeft]);

  const filteredLinked = useMemo(() => {
    const normalizedSearch = searchRight.trim().toLowerCase();

    if (!normalizedSearch) return linkedHospitais;

    return linkedHospitais.filter((h) => {
      const haystack = `${h.hospital_nome ?? ''} ${h.hospital_codigo ?? ''} ${h.hospital_cidade ?? ''} ${h.hospital_estado ?? ''}`.toLowerCase();
      // Story 2.38: busca por CNPJ normalizada (campo hospital_cnpj se disponivel)
      return haystack.includes(normalizedSearch) || cnpjMatch((h as any).hospital_cnpj, searchRight);
    });
  }, [linkedHospitais, searchRight]);

  // Handlers de seleção
  const toggleAvailableSelection = (id: number) => {
    setSelectedAvailable(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleLinkedSelection = (id: number) => {
    setSelectedLinked(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleAllAvailable = () => {
    if (selectedAvailable.length === filteredAvailable.length) {
      setSelectedAvailable([]);
    } else {
      setSelectedAvailable(filteredAvailable.map(h => h.id));
    }
  };

  const toggleAllLinked = () => {
    if (selectedLinked.length === filteredLinked.length) {
      setSelectedLinked([]);
    } else {
      setSelectedLinked(filteredLinked.map(h => h.hospital_id));
    }
  };

  // Handlers de transferência
  const handleLinkSelected = async () => {
    if (selectedAvailable.length === 0) return;

    try {
      setLinking(true);

      // Vincular todos os selecionados
      for (const hospitalId of selectedAvailable) {
        const dataInicio = new Date().toISOString().split('T')[0];
        // Gerar código automático baseado no próximo disponível
        const nextCode = await clienteService.getNextHospitalCode(clienteId);
        await clienteService.vincularHospitalSingle(clienteId, hospitalId, String(nextCode), dataInicio);
      }

      // Recarregar dados
      await loadHospitais();
      setSelectedAvailable([]);

      msg.success(`${selectedAvailable.length} hospital(is) vinculado(s) com sucesso!`);
    } catch (error: any) {
      console.error('Erro ao vincular hospitais:', error);
      msg.error(error?.response?.data?.detail || 'Erro ao vincular hospitais');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkSelected = async () => {
    if (selectedLinked.length === 0) return;

    const confirmed = await confirm({
      title: 'Desvincular Hospitais',
      message: `Deseja realmente desvincular ${selectedLinked.length} hospital(is) deste cliente?`,
      variant: 'danger',
      confirmText: 'Desvincular',
    });

    if (!confirmed) return;

    try {
      setUnlinking(true);

      // Desvincular todos os selecionados
      for (const hospitalId of selectedLinked) {
        await clienteService.desvincularHospital(clienteId, hospitalId);
      }

      // Atualizar lista local
      setLinkedHospitais(prev => prev.filter(h => !selectedLinked.includes(h.hospital_id)));
      setSelectedLinked([]);

      msg.success(`${selectedLinked.length} hospital(is) desvinculado(s) com sucesso!`);
    } catch (error: any) {
      console.error('Erro ao desvincular hospitais:', error);
      msg.error(error?.response?.data?.detail || 'Erro ao desvincular hospitais');
    } finally {
      setUnlinking(false);
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return 'Sem data';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('pt-BR');
  };

  return (
    <>
      <ConfirmDialog />
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Building2 className="w-6 h-6 text-blue-600" />
              Vincular Hospitais ao Cliente
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden p-6">
            {loadingData ? (
              <div className="py-12 flex flex-col items-center justify-center text-gray-500">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                Carregando hospitais...
              </div>
            ) : (
              <div className="flex gap-4 h-[500px]">
                {/* Coluna Esquerda: Disponíveis */}
                <div className="flex-1 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Hospitais Disponíveis
                      </h4>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">
                        {filteredAvailable.length}
                      </span>
                    </div>

                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchLeft}
                        onChange={(e) => setSearchLeft(e.target.value)}
                        placeholder="Buscar por nome, código, CNPJ, cidade..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {filteredAvailable.length > 0 && (
                      <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
                        <input
                          type="checkbox"
                          checked={selectedAvailable.length === filteredAvailable.length && filteredAvailable.length > 0}
                          onChange={toggleAllAvailable}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Selecionar todos visíveis
                      </label>
                    )}
                  </div>

                  <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {filteredAvailable.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-sm text-gray-500 dark:text-gray-400">
                        {searchLeft ? 'Nenhum hospital encontrado' : 'Todos os hospitais já estão vinculados'}
                      </div>
                    ) : (
                      filteredAvailable.map((hospital) => (
                        <label
                          key={hospital.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedAvailable.includes(hospital.id)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedAvailable.includes(hospital.id)}
                            onChange={() => toggleAvailableSelection(hospital.id)}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 dark:text-gray-100">
                              {hospital.nome_fantasia}
                            </p>
                            <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500 dark:text-gray-400">
                              <MapPin className="w-3.5 h-3.5" />
                              <span className="truncate">
                                {[hospital.codigo, hospital.cidade, hospital.estado].filter(Boolean).join(' • ')}
                              </span>
                            </div>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Botões Centrais */}
                <div className="flex flex-col items-center justify-center gap-3 w-28">
                  {/* Vincular selecionados */}
                  <button
                    onClick={handleLinkSelected}
                    disabled={selectedAvailable.length === 0 || linking}
                    className="w-full px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg disabled:shadow-none flex items-center justify-center gap-2"
                    title="Vincular selecionados"
                    type="button"
                  >
                    {linking ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <ArrowRight className="w-5 h-5 stroke-[2.5]" />
                    )}
                  </button>

                  {/* Desvincular selecionados */}
                  <button
                    onClick={handleUnlinkSelected}
                    disabled={selectedLinked.length === 0 || unlinking}
                    className="w-full px-4 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg disabled:shadow-none flex items-center justify-center gap-2"
                    title="Desvincular selecionados"
                    type="button"
                  >
                    {unlinking ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <ArrowLeft className="w-5 h-5 stroke-[2.5]" />
                    )}
                  </button>
                </div>

                {/* Coluna Direita: Vinculados */}
                <div className="flex-1 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Hospitais Vinculados
                      </h4>
                      <span className="text-xs font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded">
                        {filteredLinked.length}
                      </span>
                    </div>

                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchRight}
                        onChange={(e) => setSearchRight(e.target.value)}
                        placeholder="Buscar vinculados..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {filteredLinked.length > 0 && (
                      <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
                        <input
                          type="checkbox"
                          checked={selectedLinked.length === filteredLinked.length && filteredLinked.length > 0}
                          onChange={toggleAllLinked}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Selecionar todos visíveis
                      </label>
                    )}
                  </div>

                  <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {filteredLinked.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-sm text-gray-500 dark:text-gray-400">
                        {searchRight ? 'Nenhum hospital encontrado' : 'Nenhum hospital vinculado'}
                      </div>
                    ) : (
                      filteredLinked.map((hospital) => (
                        <label
                          key={hospital.hospital_id}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedLinked.includes(hospital.hospital_id)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedLinked.includes(hospital.hospital_id)}
                            onChange={() => toggleLinkedSelection(hospital.hospital_id)}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm text-gray-900 dark:text-gray-100 flex-1">
                                {hospital.hospital_nome}
                              </p>
                              <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
                                ✓
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500 dark:text-gray-400">
                              <MapPin className="w-3.5 h-3.5" />
                              <span className="truncate">
                                {[hospital.hospital_codigo, hospital.hospital_cidade, hospital.hospital_estado].filter(Boolean).join(' • ')}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Desde {formatDate(hospital.data_inicio)}
                            </p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Status bar */}
            {!loadingData && (selectedAvailable.length > 0 || selectedLinked.length > 0) && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  📊 {selectedAvailable.length + selectedLinked.length} hospital(is) selecionado(s)
                  {selectedAvailable.length > 0 && ` · ${selectedAvailable.length} para vincular`}
                  {selectedLinked.length > 0 && ` · ${selectedLinked.length} para desvincular`}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <button
              type="button"
              onClick={() => {
                onSave();
                onClose();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Save className="w-4 h-4" />
              Concluir
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VincularHospitaisModal;
